import type { ComputedRef } from 'vue';
import type { PlayItemRestrictionsProcessor } from "@/components/Player/types/PlayItemRestrictionsProcessor";
import {
    type OnDemandItem,
} from '@ca.vmedia.tv/domains';
import type { IButtonsIds } from "@/components/Player/components/ControlsPanel/types";
import { AppConstants } from "@/common";

interface IProps {
    isRestartBtnDisabled: ComputedRef<boolean>;
    rewindAvailable: ComputedRef<boolean>;
    fastForwardAvailable: ComputedRef<boolean>;
    itemProcessor: ComputedRef<PlayItemRestrictionsProcessor | null>;
    prevEpisode: ComputedRef<OnDemandItem | null>;
    nextEpisode: ComputedRef<OnDemandItem | null>;
    buttonsIds: IButtonsIds;
    isShowBtnsSkip: ComputedRef<boolean>;
    isBtnsLiveAndRestartAvailable: ComputedRef<boolean>;
    isLiveWithUnreachableRestart: ComputedRef<boolean>;
    showInfo: ComputedRef<boolean>;
}

interface IExportInterface {
    getBottomId(line: string): string;
    getTopId(line: string): string;
    getLeftId(currentId: string): string | null;
    getRightId(currentId: string): string | null;
}

export const useGetNavigateIds = ({
    isRestartBtnDisabled, rewindAvailable, fastForwardAvailable, itemProcessor, prevEpisode, nextEpisode,
    buttonsIds, isShowBtnsSkip, isBtnsLiveAndRestartAvailable, isLiveWithUnreachableRestart, showInfo
}: IProps) => {
    const getLeftId = (currentId: string): string | null => {
        switch (currentId) {
            case buttonsIds.btnNavBack:
                return null;

            case buttonsIds.btnRestart:
                return itemProcessor.value?.hasNavBack && prevEpisode.value ? buttonsIds.btnNavBack : getLeftId(buttonsIds.btnNavBack);

            case buttonsIds.btnSkipBack:
                return !isRestartBtnDisabled.value ? buttonsIds.btnRestart : getLeftId(buttonsIds.btnRestart);

            case buttonsIds.btnPlay:
                return rewindAvailable.value && isShowBtnsSkip.value ? buttonsIds.btnSkipBack : getLeftId(buttonsIds.btnSkipBack);

            case buttonsIds.btnSkipForward:
                return itemProcessor.value?.hasPause ? buttonsIds.btnPlay : getLeftId(buttonsIds.btnPlay);

            case buttonsIds.btnNavForward:
                return fastForwardAvailable.value ? buttonsIds.btnSkipForward : getLeftId(buttonsIds.btnSkipForward);

            case buttonsIds.btnLive:
                return itemProcessor.value?.hasNavForward && nextEpisode.value ? buttonsIds.btnNavForward : getLeftId(buttonsIds.btnNavForward);

            default:
                return null;
        }
    };
    const getRightId = (currentId: string): string | null => {
        switch (currentId) {
            case buttonsIds.btnLive:
                return null;

            case buttonsIds.btnNavForward:
                return !itemProcessor.value?.hasLive || itemProcessor.value?.liveIsPausedOnce ? buttonsIds.btnLive : null;

            case buttonsIds.btnSkipForward:
                return itemProcessor.value?.hasNavForward && nextEpisode.value ? buttonsIds.btnNavForward : getRightId(buttonsIds.btnNavForward);

            case buttonsIds.btnPlay:
                return fastForwardAvailable.value ? buttonsIds.btnSkipForward : getRightId(buttonsIds.btnSkipForward);

            case buttonsIds.btnSkipBack:
                return itemProcessor.value?.hasPause ? buttonsIds.btnPlay : getRightId(buttonsIds.btnPlay);

            case buttonsIds.btnRestart:
                return rewindAvailable.value && isShowBtnsSkip.value ? buttonsIds.btnSkipBack : getRightId(buttonsIds.btnSkipBack);

            case buttonsIds.btnNavBack:
                return !isRestartBtnDisabled.value ? buttonsIds.btnRestart : getRightId(buttonsIds.btnRestart);

            case buttonsIds.btnProgress:
                return itemProcessor.value?.hasNavBack && prevEpisode.value ? buttonsIds.btnNavBack : getRightId(buttonsIds.btnNavBack);

            default:
                return null;
        }
    };
    const getBottomId = (line: string): string | null => {
        if (line === 'top') {
            return buttonsIds.btnProgress;
        }
        if (itemProcessor.value?.hasLive && isBtnsLiveAndRestartAvailable.value && !isLiveWithUnreachableRestart.value) {
            return buttonsIds.btnRestart;
        }
        if (itemProcessor.value?.hasPause) {
            return buttonsIds.btnPlay;
        }
        if (line === buttonsIds.btnProgress && itemProcessor.value?.hasLive) {
            return AppConstants.shelfKeys.playerLinesId;
        }
        return getRightId(buttonsIds.btnProgress);
    };
    const getTopId = (line: string): string => {
        if (line === 'controls') {
            return buttonsIds.btnProgress;
        } else if (line === buttonsIds.btnProgress && showInfo.value) {
            return buttonsIds.btnInfo;
        }
        return buttonsIds.btnAdd;
    };


    return <IExportInterface> {
        getBottomId,
        getTopId,
        getLeftId,
        getRightId,
    };
};
